import { STANDARD_TIME, OVERTIME, TIME_TYPES, timeTypeCode, activeTimeOffTypes, isTimeOffType } from '@/lib/constants/timeTypes'
import { isTimeOffEligible } from '@/lib/constants/employeeTypes'

const DATE_KEY_REGEX = /^\d{2}\/\d{2}$/

// Standard time is priced from the assignment's own pay/bill rate, never from timeTypeRates
// (AssignmentEdit only ever writes an OVERTIME - or other premium - row there), so it is always
// offerable. Anything else needs both a pay and a bill rate configured on the assignment - that's
// the only combination the server's HasConfiguredRatePair check will accept a save for, so
// offering less-than-that here would let an admin fill in a row the save call then refuses.
//
// OVERTIME is withheld whenever TimeTypeBucketResolver would derive it automatically
// (overtimeEligible && !clientOfRecord, the same gate the resolver uses) - the resolver adds
// derived OT on top of any stored OT row rather than replacing it, so letting a manual OT row
// through here would double the paid/billed overtime hours.
//
// Time Off is unpriced (ADR 0014), so it is offered on Time Off Eligible assignments regardless of
// rates or pay rate type, and never otherwise.
export function offerableTypes ({ timeTypeRates, standardPayRate, overtimeEligible = false, clientOfRecord = false, employeeTypeId = null, timeTypesList = [] } = {}) {
  const derivesOvertimeAutomatically = overtimeEligible && !clientOfRecord

  const premiumTypes = (timeTypeRates || [])
    .filter(rate => !isTimeOffType(timeTypesList, rate.timeTypeId))
    .filter(rate => rate.payRate != null && rate.billRate != null)
    .filter(rate => !(derivesOvertimeAutomatically && rate.timeTypeId === OVERTIME))
    .map(rate => ({ timeTypeId: rate.timeTypeId, code: timeTypeCode(rate.timeTypeId), payRate: rate.payRate }))

  const timeOffTypes = isTimeOffEligible(employeeTypeId)
    ? activeTimeOffTypes(timeTypesList).map(type => ({ timeTypeId: type.id, code: type.name || timeTypeCode(type.id), payRate: null }))
    : []

  return [
    { timeTypeId: STANDARD_TIME, code: TIME_TYPES[STANDARD_TIME], payRate: standardPayRate },
    ...premiumTypes,
    ...timeOffTypes
  ]
}

// A week's commission record is matched by its dateBegin, normalized to midnight since the API
// returns a full timestamp. Shared between building the grid and re-matching after an edit.
export function findCommissionForWeek (commissions, weekStart) {
  return (commissions || []).find(c => {
    const dateBegin = new Date(c.dateBegin)
    dateBegin.setHours(0, 0, 0, 0)
    return dateBegin.getTime() === weekStart.getTime()
  })
}

function parseDayEntries (assignmentData) {
  return Object.keys(assignmentData)
    .filter(key => DATE_KEY_REGEX.test(key))
    .map(label => ({
      label,
      date: assignmentData[label].date,
      entries: assignmentData[label].entries || []
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
}

// Rebuilds calendar weeks from the flat MM/DD-keyed response, the same bucketing the untyped
// grid used, then splits each week into one row per time type present that week.
export function buildTypedWeeksData ({ assignmentData, beginningOfWeek = 0, timeTypeRates = [], standardPayRate = null }) {
  if (!assignmentData) return []

  const dayCells = parseDayEntries(assignmentData)
  if (!dayCells.length) return []

  const bow = beginningOfWeek
  const weekMap = new Map()

  for (const day of dayCells) {
    const entryDate = new Date(day.date)
    const dayOfWeek = entryDate.getDay()
    const dayIndex = (dayOfWeek - bow + 7) % 7

    const weekStart = new Date(entryDate)
    weekStart.setDate(entryDate.getDate() - dayIndex)
    weekStart.setHours(0, 0, 0, 0)
    const weekKey = weekStart.toISOString().split('T')[0]

    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, {
        weekKey,
        weekStart: new Date(weekStart),
        days: new Array(7).fill(null)
      })
    }

    weekMap.get(weekKey).days[dayIndex] = day
  }

  const commissions = assignmentData.contractorCommissionsPerWeek || []
  const rateByTypeId = new Map((timeTypeRates || []).map(rate => [rate.timeTypeId, rate]))

  const rateFor = (timeTypeId) => {
    if (timeTypeId === STANDARD_TIME) return standardPayRate
    return rateByTypeId.has(timeTypeId) ? rateByTypeId.get(timeTypeId).payRate : null
  }

  return Array.from(weekMap.values())
    .sort((a, b) => a.weekStart - b.weekStart)
    .map(week => {
      const weekEnd = new Date(week.weekStart)
      weekEnd.setDate(week.weekStart.getDate() + 6)

      const commission = findCommissionForWeek(commissions, week.weekStart) ||
        { contractorCommissionId: 0, contractorCommission: 0, weekNumber: 0, year: 0 }

      const typeIdsThisWeek = new Set()
      week.days.forEach(day => {
        if (!day) return
        day.entries.forEach(entry => typeIdsThisWeek.add(entry.timeTypeId))
      })

      const typeRows = Array.from(typeIdsThisWeek)
        .sort((a, b) => a - b)
        .map(timeTypeId => {
          const cells = week.days.map(day => {
            if (!day) return null
            const entry = day.entries.find(e => e.timeTypeId === timeTypeId)
            return entry ? { hours: entry.hours, timeSheetEntryId: entry.timeSheetEntryId, date: day.date, label: day.label } : null
          })

          return {
            timeTypeId,
            code: timeTypeCode(timeTypeId),
            payRate: rateFor(timeTypeId),
            cells,
            hours: cells.reduce((sum, cell) => sum + (cell && cell.hours != null ? parseFloat(cell.hours) : 0), 0)
          }
        })

      return {
        weekKey: week.weekKey,
        weekStart: week.weekStart,
        weekEnd,
        days: week.days,
        typeRows,
        contractorCommissionId: commission.contractorCommissionId,
        contractorCommission: commission.contractorCommission
      }
    })
}

export function weekHourTotals (typeRows, timeTypesList) {
  return (typeRows || []).reduce((totals, row) => isTimeOffType(timeTypesList, row.timeTypeId)
    ? { ...totals, timeOffHours: totals.timeOffHours + row.hours }
    : { ...totals, workedHours: totals.workedHours + row.hours }, { workedHours: 0, timeOffHours: 0 })
}
